import {useEffect, useState} from 'react';
import {ChevronRight} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Spinner
} from '@kroll-resolver-crisp/ui-components-react';
import {FilterChip} from '@/components/shared/FilterChip';
import {MultiSelectFilterBody} from '@/components/shared/MultiSelectFilterBody';
import {getApiErrorMessage} from '@/api/client';
import {useWhatsNew} from '@/api/hooks';
import type {WhatsNewNote, WhatsNewResponse} from '@/api/whatsNew';
import {markWhatsNewSeen, sortWhatsNew, unseenWhatsNew} from '@/whatsNew/whatsNew';

function shortDate(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-GB', {day: 'numeric', month: 'short'});
}

export function WhatsNewPage() {
  const whatsNew = useWhatsNew();

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-5 p-4">
      {whatsNew.isError ? (
        <Alert variant="destructive">
          <AlertTitle>What&apos;s new is unavailable</AlertTitle>
          <AlertDescription>{getApiErrorMessage(whatsNew.error)}</AlertDescription>
        </Alert>
      ) : whatsNew.data ? (
        <WhatsNewNotes data={whatsNew.data} />
      ) : (
        <div className="text-muted-foreground flex items-center gap-2 text-xs" role="status">
          <Spinner /> Loading…
        </div>
      )}
    </div>
  );
}

// Mounted once the notes have arrived, so "New" is read before the visit marks them seen: it means
// new to this analyst, and is gone next time.
function WhatsNewNotes({data}: {readonly data: WhatsNewResponse}) {
  const [unseen] = useState(() => unseenWhatsNew(data.notes));
  const [selected, setSelected] = useState<string[]>([]);
  // What the analyst has toggled by hand; anything absent follows the default below.
  const [toggled, setToggled] = useState<Readonly<Record<string, boolean>>>({});
  useEffect(() => {
    markWhatsNewSeen(data.notes);
  }, [data.notes]);

  const all = sortWhatsNew(data.notes);
  const areas = data.areas.filter((area) => all.some((note) => note.area === area));
  const shown = all.filter((note) => selected.length === 0 || selected.includes(note.area));

  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">
          Changes you&apos;ll notice in PRI, newest first, with a line on how to use each one. This
          page always matches the version of PRI you&apos;re using.
        </p>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <FilterChip
                active={selected.length > 0}
                label={selected.length === 0 ? 'All areas' : 'Area'}
                count={selected.length}
              />
            }
          />
          <DropdownMenuContent align="end" className="w-60">
            <MultiSelectFilterBody
              label="Areas"
              options={areas.map((area) => ({value: area, label: area}))}
              selected={selected}
              onChange={setSelected}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ol className="flex flex-col">
        {shown.map((note, index) => (
          <WhatsNewCard
            key={note.id}
            note={note}
            isNew={unseen.has(note.id)}
            open={toggled[note.id] ?? (index === 0 || unseen.has(note.id))}
            onOpenChange={(open) => {
              setToggled((current) => ({...current, [note.id]: open}));
            }}
          />
        ))}
      </ol>
      {shown.length === 0 ? (
        <p className="text-muted-foreground text-sm">No changes in the selected areas.</p>
      ) : null}
    </>
  );
}

// One note on the timeline: its date and dot in the margin, the card collapsed to its title and
// badges until opened. The newest note and any the analyst hasn't seen start open.
function WhatsNewCard({
  note,
  isNew,
  open,
  onOpenChange
}: {
  readonly note: WhatsNewNote;
  readonly isNew: boolean;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const dot = isNew ? 'bg-primary border-primary' : 'bg-card border-border';
  return (
    <li className="grid grid-cols-[4.5rem_1rem_minmax(0,1fr)] gap-3">
      <time
        dateTime={note.date}
        className="text-muted-foreground pt-3.5 text-right text-xs font-medium">
        {shortDate(note.date)}
      </time>
      <div aria-hidden className="relative">
        <span className="bg-border absolute top-0 bottom-0 left-1/2 w-px -translate-x-1/2" />
        <span
          className={`absolute top-[18px] left-1/2 size-2.5 -translate-x-1/2 rounded-full border-2 ${dot}`}
        />
      </div>
      <Collapsible
        open={open}
        className="bg-card border-border mb-3 rounded-lg border"
        onOpenChange={onOpenChange}>
        <CollapsibleTrigger
          className="flex w-full cursor-pointer items-start justify-between gap-3 p-4 text-left"
          render={<button type="button" />}>
          <span className="flex min-w-0 flex-col gap-1.5">
            <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
              {note.area}
            </span>
            <span className="text-sm font-semibold">{note.title}</span>
            <span className="flex flex-wrap items-center gap-1.5">
              {isNew ? <Badge>New</Badge> : null}
              {note.where ? <Badge variant="secondary">{note.where}</Badge> : null}
              {note.who ? <Badge variant="outline">{note.who}</Badge> : null}
            </span>
          </span>
          <ChevronRight
            aria-hidden
            className="text-muted-foreground size-4 shrink-0 transition-transform in-data-panel-open:rotate-90"
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="flex flex-col gap-2 px-4 pb-4">
            <p className="text-sm">{note.body}</p>
            {note.how ? (
              <p className="bg-muted rounded-md px-3 py-2.5 text-sm">
                <span className="font-medium">How to use it: </span>
                {note.how}
              </p>
            ) : null}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </li>
  );
}
